import { InterestPayoutDTO } from '@/models/back-office-pages/bank-statements'
import { DUNSSearchType } from '@/models/back-office-pages/duns'
import { InvoiceDetailsDTO, ReportFileDTO } from '@/models/back-office-pages/invoices'
import { LoanBookProductDetailsDTO } from '@/models/back-office-pages/loan-book'
import { AccountingNoteReportResponseDTO } from '@/models/back-office-pages/payments-reports'
import { KycFailureReason, KycIdentityRole, ProspectKycIdentityDTO } from '@/models/back-office-pages/prospects'
import { BirthType } from '@/models/back-office-pages/stop-list'
import { FromToFilterInput, MinMaxFilterInput, ReportFileDTOInput, ReportStatus } from '@/models/common'
import { Direction } from '@/models/table'

export enum OfferPayoutFrequency {
  ANNUALLY = 'ANNUALLY',
  END_OF_TERM = 'END_OF_TERM',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
}

export enum OfferType {
  ASSET_BASED = 'ASSET_BASED',
  CORPORATE_NOTE = 'CORPORATE_NOTE',
  GOVERNMENT_BACKED = 'GOVERNMENT_BACKED',
}

export enum OfferFilterStatus {
  ALL = 'ALL',
  ACTIVE = 'ACTIVE',
  FULLY_USED = 'FULLY_USED',
  INACTIVE = 'INACTIVE',
  NEW = 'NEW',
}

export enum OfferStatus {
  ACTIVE = 'ACTIVE',
  FULLY_USED = 'FULLY_USED',
  INACTIVE = 'INACTIVE',
  NEW = 'NEW',
}

export interface OfferDTO {
  annualInterestRate: number
  bookedAmount: number
  availableAmount: number
  collectedAmount: number
  earlyWithdrawalRate: number
  endDate: Date
  id: string
  offerNumber: string
  interestPayoutFrequency: OfferPayoutFrequency
  investmentTerm: number
  offerType: OfferType
  startDate: Date
  status: OfferStatus
  totalAmountToBorrow: number
  minInvestmentAmount: number
}

export interface Page_OfferDTO {
  content: Array<OfferDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface OffersData {
  offerList: Page_OfferDTO
}

export interface OffersVariables {
  interestPayoutFrequency?: Array<OfferPayoutFrequency>
  offerType?: Array<OfferType>
  tableStatus: OfferFilterStatus
  bookedAmount?: MinMaxFilterInput
  totalAmountToBorrow?: MinMaxFilterInput
  collectedAmount?: MinMaxFilterInput
  status?: Array<OfferFilterStatus>
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface CreateNewOfferData {
  createNewOffer: Page_OfferDTO
}

export interface CreateNewOfferVariables {
  offerType: OfferType
  annualInterestRate: number
  investmentTerm: number
}

export enum CreateNewOfferFieldNames {
  TYPE = 'offerType',
  INVESTMENT_TERM = 'investmentTerm',
  ANNUAL_INTEREST_RATE = 'annualInterestRate',
}

export enum AuditActions {
  OFFER_CREATED = 'OFFER_CREATED',
  OFFER_EDIT_BALANCE = 'OFFER_EDIT_BALANCE',
  OFFER_EDIT_DESCRIPTION = 'OFFER_EDIT_DESCRIPTION',
  OFFER_EDIT_DETAILS = 'OFFER_EDIT_DETAILS',
  OFFER_EDIT_SECURITY_DESCRIPTION = 'OFFER_EDIT_SECURITY_DESCRIPTION',
  INVESTOR_ACTIVATED = 'INVESTOR_ACTIVATED',
  INVESTOR_APPROVAL_REQUESTED = 'INVESTOR_APPROVAL_REQUESTED',
  INVESTOR_APPROVED = 'INVESTOR_APPROVED',
  INVESTOR_ARCHIVED = 'INVESTOR_ARCHIVED',
  INVESTOR_CONTACT_INFO_UPDATED = 'INVESTOR_CONTACT_INFO_UPDATED',
  INVESTOR_CREATED = 'INVESTOR_CREATED',
  INVESTOR_DETAILS_UPDATED = 'INVESTOR_DETAILS_UPDATED',
  INVESTOR_KYC_IDENTITY_ADDED = 'INVESTOR_KYC_IDENTITY_ADDED',
  INVESTOR_KYC_IDENTITY_FACTRIS_APPROVED = 'INVESTOR_KYC_IDENTITY_FACTRIS_APPROVED',
  INVESTOR_KYC_IDENTITY_IDENTIFICATION_UPDATED = 'INVESTOR_KYC_IDENTITY_IDENTIFICATION_UPDATED',
  INVESTOR_KYC_IDENTITY_RISK_QUESTIONNAIRE_UPDATED = 'INVESTOR_KYC_IDENTITY_RISK_QUESTIONNAIRE_UPDATED',
  INVESTOR_LEGAL_REPRESENTATIVE_ADDED = 'INVESTOR_LEGAL_REPRESENTATIVE_ADDED',
  INVESTOR_LEGAL_REPRESENTATIVE_UPDATED = 'INVESTOR_LEGAL_REPRESENTATIVE_UPDATED',
  INVESTOR_ORIGIN_OF_MONEY_UPDATED = 'INVESTOR_ORIGIN_OF_MONEY_UPDATED',
  INVESTOR_ORIGIN_OF_MONEY_VERIFIED = 'INVESTOR_ORIGIN_OF_MONEY_VERIFIED',
  INVESTOR_RESET_TO_NEW = 'INVESTOR_RESET_TO_NEW',
  INVESTOR_RISK_QUESTIONNAIRE_UPDATED = 'INVESTOR_RISK_QUESTIONNAIRE_UPDATED',
  INVESTOR_SETTINGS_UPDATED = 'INVESTOR_SETTINGS_UPDATED',
  INVESTOR_UBO_ADDED = 'INVESTOR_UBO_ADDED',
  INVESTOR_UBO_DELETED = 'INVESTOR_UBO_DELETED',
  INVESTOR_UBO_UPDATED = 'INVESTOR_UBO_UPDATED',
  INVESTMENT_TASK_ADD_REINVESTMENT = 'INVESTMENT_TASK_ADD_REINVESTMENT',
  INVESTMENT_TASK_ARCHIVED = 'INVESTMENT_TASK_ARCHIVED',
  INVESTMENT_TASK_ASSIGNED = 'INVESTMENT_TASK_ASSIGNED',
  INVESTMENT_TASK_CANCELED_REINVESTMENT = 'INVESTMENT_TASK_CANCELED_REINVESTMENT',
  INVESTMENT_TASK_CLOSED = 'INVESTMENT_TASK_CLOSED',
  INVESTMENT_TASK_CREATED = 'INVESTMENT_TASK_CREATED',
  INVESTMENT_TASK_RESCHEDULED = 'INVESTMENT_TASK_RESCHEDULED',
  INVESTMENT_TASK_RESET_TO_NEW = 'INVESTMENT_TASK_RESET_TO_NEW',
  INVESTMENT_TASK_UPLOADED_SIGNED_DOCUMENT = 'INVESTMENT_TASK_UPLOADED_SIGNED_DOCUMENT',
  INVESTMENT_TASK_SENT_SPECIAL_CONDITIONS = 'INVESTMENT_TASK_SENT_SPECIAL_CONDITIONS',
}

export interface OfferLogDTO {
  id: string
  userFullName: string
  event: AuditActions
  createdAt: Date
}

export interface OfferActivationCheckListDTO {
  hasBalanceSet: boolean
  hasCommercialTermsSet: boolean
}

export interface OfferDetailsDTO {
  annualInterestRate: number
  totalAmountToBorrow: number
  availableAmount: number
  bookedAmount: number
  collectedAmount: number
  description: string
  earlyWithdrawalRate: number
  endDate: string
  id: string
  offerNumber: string
  interestPayoutFrequency: OfferPayoutFrequency
  investmentTerm: number
  offerType: OfferType
  securityDescription: string
  startDate: string
  status: OfferStatus
  riskClass: string
  minInvestmentAmount: number
  activationCheckList: OfferActivationCheckListDTO
  logs: Array<OfferLogDTO>
}

export interface OfferDetailsData {
  offerDetails: OfferDetailsDTO
}

export interface OfferDetailsVariables {
  offerId: string
}

export interface ActivateOfferData {
  activateOffer: OfferDetailsDTO
}

export interface ActivateOfferVariables {
  offerId: string
}

export interface DeactivateOfferData {
  deactivateOffer: OfferDetailsDTO
}

export interface DeactivateOfferVariables {
  offerId: string
}

export interface UpdateOfferBalanceData {
  updateOfferBalance: OfferDetailsDTO
}

export interface UpdateOfferBalanceVariables {
  offerId: string
  totalAmountToBorrow: number
}

export interface UpdateOfferBalanceFormValues {
  bookedAmount: number
  collectedAmount: number
  totalAmountToBorrow: number
  availableAmount: number
}

export enum UpdateOfferBalanceFieldsNames {
  TOTAL_AMOUNT = 'totalAmountToBorrow',
  BOOKED_AMOUNT = 'bookedAmount',
  COLLECTED_AMOUNT = 'collectedAmount',
  AVAILABLE_AMOUNT = 'availableAmount',
}

export interface UpdateOfferDescriptionData {
  updateOfferDescription: OfferDetailsDTO
}

export interface UpdateOfferDescriptionVariables {
  summary: string
  id: string
}

export interface UpdateOfferSecurityDescriptionData {
  updateOfferSecurityDescription: OfferDetailsDTO
}

export interface UpdateOfferSecurityDescriptionVariables {
  summary: string
  id: string
}

export interface UpdateOfferDetailsData {
  updateOfferDetails: OfferDetailsDTO
}

export interface UpdateOfferDetailsFormValues {
  interestPayoutFrequency: OfferPayoutFrequency
  offerType: OfferType
  riskClass: string
  endDate: string
  annualInterestRate: number
  investmentTerm: number
  minInvestmentAmount: number
  startDate: string
  earlyWithdrawalRate: number
}

export interface UpdateOfferDetailsVariables extends UpdateOfferDetailsFormValues {
  offerId: string
}

export enum UpdateOfferDetailsFieldsNames {
  TYPE = 'offerType',
  STATUS = 'status',
  INVESTMENT_TERM = 'investmentTerm',
  ANNUAL_INTEREST_RATE = 'annualInterestRate',
  RISK_CLASS = 'riskClass',
  EARLY_WITHDRAWAL_RATE = 'earlyWithdrawalRate',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  INTEREST_PAYOUT_FREQUENCY = 'interestPayoutFrequency',
  MIN_INVESTMENT_AMOUNT = 'minInvestmentAmount',
}

export enum InvestorStatus {
  NEW = 'NEW',
  IN_RISK_APPROVAL = 'IN_RISK_APPROVAL',
  RISK_APPROVED = 'RISK_APPROVED',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum InvestorFilterStatus {
  ALL = 'ALL',
  NEW = 'NEW',
  IN_RISK_APPROVAL = 'IN_RISK_APPROVAL',
  RISK_APPROVED = 'RISK_APPROVED',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export interface InvestorDTO {
  availableBalanceAmount: number
  balanceAmount: number
  country: string
  id: string
  identificationNumber: string
  investorType: InvestorType
  name: string
  status: InvestorStatus
  totalAmountInvest: number
}

export interface Page_InvestorDTO {
  content: Array<InvestorDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface InvestorsData {
  investorList: Page_InvestorDTO
}

export interface InvestorsVariables {
  country?: string
  sortDirection?: Direction
  sortField?: string
  totalAmountInvest?: MinMaxFilterInput
  name?: string
  pageSize?: number
  identificationNumber?: string
  pageNum?: number
  status?: Array<InvestorStatus>
  tableStatus: InvestorFilterStatus
  investorType?: Array<InvestorType>
  offerId?: string
}

export interface AddNewInvestorData {
  createNewInvestor: InvestorDTO
}

export enum InvestorType {
  COMPANY = 'COMPANY',
  NATURAL_PERSON = 'NATURAL_PERSON',
}

export interface AddNewInvestorVariables {
  investorType: InvestorType
  dunId: string
  countryOfResidence: string
  firstName: string
  surname: string
  email: string
  phone: string
  investmentTaskId?: string
}

export interface AddNewInvestorFormValues {
  investorType: InvestorType | string
  dunId: string
  companyCountryCode: string
  countryOfResidence: string
  searchType: DUNSSearchType
  firstName: string
  surname: string
  email: string
  phone: string
}

export enum AddNewInvestorFieldNames {
  TYPE = 'investorType',
  COUNTRY_OF_RESIDENCE = 'countryOfResidence',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'surname',
  EMAIL = 'email',
  PHONE = 'phone',
}

export enum InvestorGender {
  FEMALE = 'FEMALE',
  MALE = 'MALE',
}

export interface InvestorApprovalChecklistDTO {
  hasAllAmountInvested: boolean
  hasCompanyAndUboStructureSet: boolean
  hasContactInformationSet: boolean
  hasInvestorIdSet: boolean
  hasKycIdentificationApproved: boolean
  hasKycIdentificationInitiated: boolean
  hasLegalRepresentativeKycInitiated: boolean
  hasLegalRepresentativeSet: boolean
  hasOriginOfMoneySet: boolean
  hasOriginOfMoneyVerified: boolean
  hasPersonalDetailsSet: boolean
  hasReceivedAmountsForInvesting: boolean
  hasRiskQuestionnaireBeFilled: boolean
  hasSpecifiedSettings: boolean
  hasUboListSet: boolean
  hasUploadedContract: boolean
  hasUploadedCreditReport: boolean
  idDocumentMustBeUploaded: boolean
  hasInvestorManagerAssigned: boolean
}

export interface InvestorContactInfoDTO {
  contactEmail: string
  contactPerson: string
  contactPhone: string
  preferenceLanguage: string
}

export interface InvestorLogDTO {
  id: string
  createdAt: Date
  event: AuditActions
  userFullName: string
  comment: string
  originOfMoneyVerificationComment: string
  originOfMoneyApprovedPartlyComment: string
  originOfMoneyAmount: number
  investmentNumber: string
  investmentPlanId: string
}

export enum InvestorOriginOfMoney {
  COMPENSATION_LEGAL_RULINGS = 'COMPENSATION_LEGAL_RULINGS',
  GAMBLING_WINNINGS = 'GAMBLING_WINNINGS',
  INHERITANCE = 'INHERITANCE',
  INVESTMENTS = 'INVESTMENTS',
  PENSION = 'PENSION',
  PERSONAL_SAVINGS = 'PERSONAL_SAVINGS',
  SALARY = 'SALARY',
  SHARE_SALES = 'SHARE_SALES',
  SOLD_ASSETS = 'SOLD_ASSETS',
}

export interface InvestorOriginOfMoneyDTO {
  files: ReportFileDTO[]
  originOfMoneyList: InvestorOriginOfMoney[]
  originOfMoneyAmount: number
  originOfMoneyVerificationComment: string
  originOfMoneyVerified: boolean
  originOfMoneyVerifiedAt: Date
  originOfMoneyVerifiedBy: string
  originOfMoneyVerifiedByEmail: string
}

export interface InvestorIbanDTO {
  holderName: string
  iban: string
  id: string
  mainAccount: boolean
}

export interface InvestorSettingsDTO {
  investorIbans: Array<InvestorIbanDTO>
  interestPayoutFrequency: OfferPayoutFrequency
  settingsAutoProlongation: boolean
  settingsReinvestInterest: boolean
}

export interface InvestorInfoDetailsDTO {
  id: string
  name: string
  investorType: InvestorType
  status: InvestorStatus
  addressLine1: string
  addressLine2: string
  city: string
  postalCode: string
  country: string
  birthYear: number
  dateOfBirth: string
  legalStructure: string
  legalStructureTypeId: string
  personFirstName: string
  personSurname: string
  personalId: string
  gender: InvestorGender
  registrationNumber: string
  vatNumber: string
}

export interface InvestorDunsDetailsDTO {
  city: string
  country: string
  dunReportCreated: Date
  dunsId: string
  hooversIndustryCode: string
  naceIndustryCode: string
  numberOfEmployees: number
  postalCode: string
  rawData: string
  registrationNumber: string
  standardIndustryCode: string
  startDate: string
  streetAddressLine1: string
  streetAddressLine2: string
  vatNumber: string
  phoneNumbers: string
}

export interface InvestorApprovalDTO {
  approvalComment: string
  approvalExternalComment: string
  approvalStatus: InvestorStatus
  approver: string
  approverEmail: string
  decisionMadeOn: Date
}

export enum InvestorPaymentType {
  DEPOSIT = 'DEPOSIT',
  INVESTMENT = 'INVESTMENT',
  REPAYMENT = 'REPAYMENT',
  WITHDRAWAL = 'WITHDRAWAL',
  WITHDRAWAL_BEFORE_TERM = 'WITHDRAWAL_BEFORE_TERM',
}

export enum InvestorWithdrawalPaymentStatus {
  archived = 'archived',
  blocked = 'blocked',
  init = 'init',
  paid = 'paid',
  processing = 'processing',
}

export interface InvestorBalancePaymentDTO {
  date: string
  id: string
  investedAmount: number
  investmentNumber: string
  investmentPlanId: string
  paymentType: InvestorPaymentType
  receivedAmount: number
  penalty: number
  comment: string
  withdrawalPaymentStatus: InvestorWithdrawalPaymentStatus
}

export interface InvestorBalanceDetailsDTO {
  availableAmount: number
  totalInvested: number
  totalReceived: number
  availableAmountForInvestment: number
  payments: Array<InvestorBalancePaymentDTO>
}

export interface InvestOriginOfMoneyApprovedPartlyDTO {
  originOfMoneyApprovedPartly: boolean
  originOfMoneyApprovedPartlyComment: string
}

export interface InvestorMismatchedBeneficiaryDTO {
  holderName: string
  iban: string
  investorName: string
}

export interface InvestorDetailsDTO {
  managerEmail: string
  approvalChecklist: InvestorApprovalChecklistDTO
  contactInfo: InvestorContactInfoDTO
  dunsDetails: InvestorDunsDetailsDTO
  balanceDetails: InvestorBalanceDetailsDTO
  mismatchedBeneficiaries: Array<InvestorMismatchedBeneficiaryDTO>
  id: string
  investorDetails: InvestorInfoDetailsDTO
  logs: Array<InvestorLogDTO>
  originOfMoney: InvestorOriginOfMoneyDTO
  originOfMoneyApprovedPartly: InvestOriginOfMoneyApprovedPartlyDTO
  settings: InvestorSettingsDTO
  approval: InvestorApprovalDTO
}

export interface InvestorDetailsData {
  investorDetails: InvestorDetailsDTO
}

export interface InvestorDetailsVariables {
  investorId: string
}

export interface UpdateInvestorDetailsData {
  updateInvestorsDetails: InvestorDetailsDTO
}

export interface CancelInvestorsMismatchedBeneficiaryVariables {
  investorId: string
}

export interface CancelInvestorsMismatchedBeneficiaryData {
  cancelInvestorsMismatchedBeneficiary: InvestorDetailsDTO
}

export interface UpdateInvestorDetailsFormValues {
  investorType: InvestorType
  status: InvestorStatus
  addressLine1: string
  addressLine2: string
  city: string
  postalCode: string
  country: string
  personFirstName: string
  personSurname: string
  gender: InvestorGender
  dateOfBirth: string
  birthType?: BirthType
  birthYear: number
  personalId: string
  legalStructureTypeId: string
  legalStructure: string
  registrationNumber: string
  vatNumber: string
}

export interface UpdateInvestorDetailsVariables extends UpdateInvestorDetailsFormValues {
  investorId: string
}

export enum InvestorDetailsFieldsNames {
  TYPE = 'investorType',
  STATUS = 'status',
  FULL_ADDRESS = 'fullAddress',
  STREET_1 = 'addressLine1',
  STREET_2 = 'addressLine2',
  CITY = 'city',
  POSTAL_CODE = 'postalCode',
  COUNTRY = 'country',
  FIRST_NAME = 'personFirstName',
  LAST_NAME = 'personSurname',
  GENDER = 'gender',
  BIRTH_TYPE = 'birthType',
  BIRTH_YEAR = 'birthYear',
  BIRTH_DATE = 'dateOfBirth',
  PERSONAL_ID = 'personalId',
  LEGAL_STRUCTURE_ID = 'legalStructureTypeId',
  COC = 'registrationNumber',
  VAT = 'vatNumber',
}

export interface UpdateInvestorsSettingsData {
  updateInvestorsSettings: InvestorDetailsDTO
}

export interface UpdateInvestorsSettingsFormValues {
  interestPayoutFrequency: OfferPayoutFrequency
  settingsAutoProlongation: boolean
  settingsReinvestInterest: boolean
}

export interface UpdateInvestorsSettingsVariables extends UpdateInvestorsSettingsFormValues {
  investorId: string
}

export enum UpdateInvestorsSettingsFieldsNames {
  INTEREST_PAYOUT_FREQUENCY = 'interestPayoutFrequency',
  AUTO_PROLONGATION = 'settingsAutoProlongation',
  REINVEST_INTEREST = 'settingsReinvestInterest',
}

export interface UpdateInvestorsContactInformationData {
  updateInvestorsContactInformation: InvestorDetailsDTO
}

export interface UpdateInvestorsContactInformationFormValues {
  contactEmail: string
  contactPerson: string
  preferenceLanguage: string
  contactPhone: string
}

export interface UpdateInvestorsContactInformationVariables extends UpdateInvestorsContactInformationFormValues {
  investorId: string
}

export enum UpdateInvestorsContactInformationFieldsNames {
  NAME = 'contactPerson',
  EMAIL = 'contactEmail',
  PHONE = 'contactPhone',
  PREFERENCE_LANGUAGE = 'preferenceLanguage',
}

export interface LegalStructureTypesDTO {
  countryCode: string
  id: string
  name: string
}

export interface InvestorLegalStructuresData {
  investorLegalStructures: Array<LegalStructureTypesDTO>
}

export interface InvestorLegalStructuresVariables {
  investorId: string
}

export interface UpdateInvestorSettingsData {
  updateInvestorsSettings: InvestorDetailsDTO
}

export interface UpdateInvestorSettingsFormValues {
  interestPayoutFrequency: OfferPayoutFrequency
  settingsAutoProlongation: boolean
  settingsReinvestInterest: boolean
  mainAccountId: string
}

export interface UpdateInvestorSettingsVariables extends UpdateInvestorSettingsFormValues {
  investorId: string
}

export enum UpdateInvestorSettingsFieldsNames {
  INTEREST_PAYOUT_FREQUENCY = 'interestPayoutFrequency',
  AUTO_PROLONGATION = 'settingsAutoProlongation',
  REINVEST_INTEREST = 'settingsReinvestInterest',
  MAIN_IBAN_ID = 'mainAccountId',
}

export interface UpdateInvestorOriginOfMoneyData {
  updateInvestorsOriginOfMoney: InvestorDetailsDTO
}

export interface UpdateInvestorOriginOfMoneyFormValues {
  originOfMoneyList: InvestorOriginOfMoney[]
  originOfMoneyVerificationComment: string
  originOfMoneyApprovedPartlyComment?: string
  originOfMoneyVerified: boolean
  originOfMoneyAmount: number
  totalReceived?: number
  amountToReturn?: number
}

export interface UpdateInvestorOriginOfMoneyVariables extends UpdateInvestorOriginOfMoneyFormValues {
  investorId: string
}

export enum UpdateInvestorOriginOfMoneyFieldsNames {
  TOTAL_RECEIVED = 'totalReceived',
  ORIGIN_OF_MONEY = 'originOfMoneyList',
  AMOUNT = 'originOfMoneyAmount',
  AMOUNT_TO_RETURN = 'amountToReturn',
  VERIFIED = 'originOfMoneyVerified',
  VERIFIED_COMMENT = 'originOfMoneyVerificationComment',
  RETURN_COMMENT = 'originOfMoneyApprovedPartlyComment',
}

export interface DownloadInvestorDocumentData {
  downloadInvestorDocument: ReportFileDTOInput
}

export interface DownloadInvestorDocumentVariables {
  documentId: string
}

export interface UploadInvestorDocumentData {
  uploadInvestorDocument: string
}

export enum InvestorDocumentType {
  APPLICATION_FORM = 'APPLICATION_FORM',
  COMPANY_AND_UBO_STRUCTURE = 'COMPANY_AND_UBO_STRUCTURE',
  CONTRACT = 'CONTRACT',
  CREDIT_REPORT = 'CREDIT_REPORT',
  ID = 'ID',
  KYC_DOCUMENT = 'KYC_DOCUMENT',
  OFFER = 'OFFER',
  ORIGIN_OF_MONEY = 'ORIGIN_OF_MONEY',
  OTHER = 'OTHER',
}

export interface UploadInvestorDocumentVariables {
  reportType: InvestorDocumentType
  investorId: string
  file: ReportFileDTOInput
  reportCreatedAt: string
  description: string
}

export interface UploadInvestorDocumentFormValues {
  reportType: InvestorDocumentType
  investorId: string
  files: ReportFileDTOInput[]
  reportCreatedAt: string
  description: string
}

export interface DeleteInvestorDocumentData {
  deleteInvestorDocument: InvestorDocumentDTO
}

export interface DeleteInvestorDocumentVariables {
  documentId: string
}

export interface InvestorDocumentDTO {
  investorId: string
  content: string
  description: string
  documentId: string
  fileName: string
  reportCreatedAt: string
  reportType: InvestorDocumentType
  uploader: string
}

export interface Page_InvestorDocumentDTO {
  content: InvestorDocumentDTO[]
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface InvestorDocumentsData {
  investorDocumentList: Page_InvestorDocumentDTO
}

export interface InvestorDocumentsVariables {
  investorId: string
  sortDirection?: Direction
  sortField?: string
  pageSize?: number
  pageNum?: number
}

export interface UpdateInvestorDunsDetailsData {
  updateInvestorDunsDetails: InvestorDetailsDTO
}

export interface UpdateInvestorDunsDetailsVariables {
  investorId: string
}

export enum InvestorActionType {
  REQUEST_APPROVAL = 'REQUEST_APPROVAL',
  APPROVE = 'APPROVE',
  ACTIVATE = 'ACTIVATE',
  ARCHIVE = 'ARCHIVE',
  RESET_TO_NEW = 'RESET_TO_NEW',
}

export enum InvestorActionFieldsSchema {
  ID = 'id',
  COMMENT = 'comment',
  ACTION_TYPE = 'actionType',
  ARCHIVE_REASON = 'archiveReason',
}

export enum InvestorArchiveReason {
  REJECTED_BY_FACTRIS = 'REJECTED_BY_FACTRIS',
  REJECTED_BY_INVESTOR = 'REJECTED_BY_INVESTOR',
}

export interface InvestorActionFormValues {
  comment: string
  actionType: InvestorActionType
  archiveReason: InvestorArchiveReason
}

export interface ApproveInvestorData {
  approveInvestor: InvestorDetailsDTO
}

export interface ApproveInvestorVariables {
  investorId: string
  comment: string
}

export interface ActivateInvestorData {
  activateInvestor: InvestorDetailsDTO
}

export interface ActivateInvestorVariables {
  investorId: string
  comment: string
}

export interface ArchiveInvestorData {
  archiveInvestor: InvestorDetailsDTO
}

export interface ArchiveInvestorVariables {
  investorId: string
  comment: string
  archiveReason: InvestorArchiveReason
}

export interface RequestInvestorApprovalData {
  requestApprovalInvestor: InvestorDetailsDTO
}

export interface RequestInvestorApprovalVariables {
  investorId: string
  comment: string
}

export interface ResetInvestorToNewData {
  resetToNewInvestor: InvestorDetailsDTO
}

export interface ResetInvestorToNewVariables {
  investorId: string
  comment: string
}

export interface InvestorCommentDTO {
  archived: boolean
  investorId: string
  comment: string
  createdBy: string
  createdByEmail: string
  createdById: string
  createdOn: Date
  legacy: boolean
  flagged: boolean
  id: string
  updatedBy: string
  updatedByEmail: string
  updatedById: string
  updatedOn: Date
}

export interface GetInvestorCommentsData {
  getInvestorComments: Array<InvestorCommentDTO>
}

export interface GetInvestorCommentsVariables {
  investorId: string
}

export interface GetInvestorCommentByIdData {
  getInvestorCommentById: InvestorCommentDTO
}

export interface GetInvestorCommentsByIdVariables {
  commentId: string
}

export interface AddInvestorCommentData {
  addInvestorComment: InvestorCommentDTO
}

export interface AddInvestorCommentVariables {
  investorId: string
  comment: string
}

export interface UpdateInvestorCommentData {
  updateInvestorComment: InvestorCommentDTO
}

export interface UpdateInvestorCommentVariables {
  comment: string
  commentId: string
}

export interface MarkInvestorCommentAsFlaggedData {
  markInvestorCommentAsFlagged: InvestorCommentDTO
}

export interface MarkInvestorCommentAsFlaggedVariables {
  commentId: string
}

export interface MarkInvestorCommentAsUnflaggedData {
  markInvestorCommentAsUnflagged: InvestorCommentDTO
}

export interface MarkInvestorCommentAsUnflaggedVariables {
  commentId: string
}

export interface MarkInvestorCommentAsArchivedData {
  markInvestorCommentAsArchived: InvestorCommentDTO
}

export interface MarkInvestorCommentAsArchivedVariables {
  commentId: string
}

export interface MarkInvestorCommentAsUnarchivedData {
  markInvestorCommentAsUnarchived: InvestorCommentDTO
}

export interface MarkInvestorCommentAsUnarchivedVariables {
  commentId: string
}

export enum KycIdentificationType {
  FACTRIS = 'FACTRIS',
  ONDATO = 'ONDATO',
}

export enum KycQuestionnaireStatus {
  APPROVED = 'APPROVED',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
  SUCCESSFUL = 'SUCCESSFUL',
}

export interface InvestorKycIdentityDTO {
  birthYear: number
  communicationCountry: string
  country: string
  creatorEmail: string
  email: string
  failureReason: KycFailureReason
  id: string
  investorId: string
  kycIdentificationType: KycIdentificationType
  name: string
  ondatoBackOfficeStatusUrl: string
  questionnaireFile: string
  questionnaireId: string
  questionnaireUrl: string
  role: KycIdentityRole
  status: KycQuestionnaireStatus
  surname: string
  factrisIdentificationApproved: boolean
}

export interface InvestorKycLogDTO {
  id: string
  createdAt: Date
  event: AuditActions
  userFullName: string
}

export interface InvestorUboDTO {
  addressLine1: string
  addressLine2: string
  dateOfBirth: string
  birthYear: number
  city: string
  comment: string
  country: string
  formOfControl: InvestorUboFormOfControl
  gender: InvestorGender
  id?: string
  investorId?: string
  name: string
  postalCode: string
  role: KycIdentityRole
  sharesValue: number
  surname: string
}

export interface InvestorRiskQuestionnaireDTO {
  id: string
  companyHitInOndato: boolean
  companyHitInOndatoComment: string
  legalRepresentativeHitInOndato: boolean
  legalRepresentativeHitInOndatoComment: string
}

export interface InvestorKycDetailsDTO {
  investorKycIdentities: Array<InvestorKycIdentityDTO>
  ubos: Array<InvestorUboDTO>
  legalRepresentatives: Array<InvestorUboDTO>
  riskQuestionnaire: InvestorRiskQuestionnaireDTO
  logs: Array<InvestorKycLogDTO>
}

export interface InvestorKycDetailsData {
  investorKycDetails: InvestorKycDetailsDTO
}

export interface InvestorKycDetailsVariables {
  investorId: string
}

export interface AddInvestorKycIdentityData {
  addInvestorKycIdentity: InvestorKycIdentityDTO
}

export interface AddInvestorKycIdentityFormValues {
  role?: KycIdentityRole
  surname: string
  name: string
  naturalPerson?: string
  kycIdentificationType: KycIdentificationType
  email: string
  communicationCountry: string
  country: string
  birthYear: number
  dateOfBirth?: string
}

export enum AddInvestorKycIdentityFieldsNames {
  NATURAL_PERSON = 'naturalPerson',
  NAME = 'name',
  SURNAME = 'surname',
  IDENTIFICATION_TYPE = 'kycIdentificationType',
  ROLE = 'role',
  EMAIL = 'email',
  COUNTRY_OF_RESIDENCE = 'country',
  PREFERRED_LANGUAGE = 'communicationCountry',
  BIRTH_TYPE = 'birthType',
  BIRTH_YEAR = 'birthYear',
  BIRTH_DATE = 'birthDate',
}

export interface AddInvestorKycIdentityVariables extends AddInvestorKycIdentityFormValues {
  investorId: string
}

export interface AddInvestorLegalRepresentativeData {
  addInvestorLegalRepresentative: InvestorKycDetailsDTO
}

export enum InvestorUboFormOfControl {
  CONTROL_BY_OTHER_MEANS = 'CONTROL_BY_OTHER_MEANS',
  MEMBER_OF_BOARD = 'MEMBER_OF_BOARD',
  SHARES = 'SHARES',
  VOTING_RIGHTS = 'VOTING_RIGHTS',
}

export interface AddInvestorLegalRepresentativeFormValues {
  surname: string
  name: string
  gender: InvestorGender
  addressLine1: string
  addressLine2: string
  country: string
  city: string
  postalCode: string
  birthType?: BirthType
  dateOfBirth: string
  birthYear: number
  isUboAsWell?: boolean
  formOfControl: InvestorUboFormOfControl
  sharesValue: number
  comment: string
}

export enum InvestorLegalRepresentativeFieldsNames {
  FIRST_NAME = 'name',
  LAST_NAME = 'surname',
  GENDER = 'gender',
  STREET_1 = 'addressLine1',
  STREET_2 = 'addressLine2',
  CITY = 'city',
  POSTAL_CODE = 'postalCode',
  COUNTRY = 'country',
  BIRTH_TYPE = 'birthType',
  BIRTH_YEAR = 'birthYear',
  BIRTH_DATE = 'dateOfBirth',
  IS_UBO = 'isUboAsWell',
  FORM_OF_CONTROL = 'formOfControl',
  SHARES_VALUE = 'sharesValue',
  COMMENT = 'comment',
}

export interface AddInvestorLegalRepresentativeVariables extends AddInvestorLegalRepresentativeFormValues {
  investorId: string
}

export interface EditInvestorLegalRepresentativeData {
  updateInvestorLegalRepresentative: InvestorKycDetailsDTO
}

export interface EditInvestorLegalRepresentativeVariables extends AddInvestorLegalRepresentativeFormValues {
  id: string
}

export interface AddInvestorUboData {
  addInvestorUbo: InvestorKycDetailsDTO
}

export interface AddInvestorUboFormValues {
  surname: string
  name: string
  country: string
  birthType?: BirthType
  birthYear: number
  dateOfBirth: string
  formOfControl: InvestorUboFormOfControl
  sharesValue: number
  comment: string
}

export enum AddInvestorUboFieldsNames {
  FIRST_NAME = 'name',
  LAST_NAME = 'surname',
  COUNTRY = 'country',
  BIRTH_TYPE = 'birthType',
  BIRTH_YEAR = 'birthYear',
  BIRTH_DATE = 'dateOfBirth',
  FORM_OF_CONTROL = 'formOfControl',
  SHARES_VALUE = 'sharesValue',
  COMMENT = 'comment',
}

export interface AddInvestorUboVariables extends AddInvestorUboFormValues {
  investorId: string
}

export interface EditInvestorUboData {
  updateInvestorUbo: InvestorKycDetailsDTO
}

export interface EditInvestorUboVariables extends AddInvestorUboFormValues {
  id: string
}

export interface DeleteInvestorUboData {
  deleteInvestorUbo: InvestorKycDetailsDTO
}

export interface DeleteInvestorUboVariables {
  id: string
}

export interface DeleteInvestorKycIdentityData {
  deleteInvestorKycIdentity: InvestorKycIdentityDTO
}

export interface DeleteInvestorKycIdentityVariables {
  identityId: string
}

export interface CheckInvestorKycIdentityData {
  checkProspectKycStatus: Array<ProspectKycIdentityDTO>
}

export interface CheckInvestorKycIdentityVariables {
  id: string
}

export interface StartInvestorKycData {
  startProspectKyc: Array<ProspectKycIdentityDTO>
}

export interface StartInvestorKycVariables {
  id: string
}

export enum InvestorRiskQuestionnaireFieldsNames {
  COMPANY_HIT_IN_ONDATO = 'companyHitInOndato',
  COMPANY_HIT_IN_ONDATO_COMMENT = 'companyHitInOndatoComment',
  LEGAL_REPRESENTATIVE_HIT_IN_ONDATO = 'legalRepresentativeHitInOndato',
  LEGAL_REPRESENTATIVE_HIT_IN_ONDATO_COMMENT = 'legalRepresentativeHitInOndatoComment',
}

export interface InvestorRiskQuestionnaireFormValues {
  companyHitInOndato: boolean
  companyHitInOndatoComment: string
  legalRepresentativeHitInOndato: boolean
  legalRepresentativeHitInOndatoComment: string
}

export interface InvestorRiskQuestionnaireVariables extends InvestorRiskQuestionnaireFormValues {
  investorId: string
}

export interface InvestorRiskQuestionnaireData {
  updateInvestorRiskQuestionnaire: InvestorKycDetailsDTO
}

export enum ManualKycIdentificationType {
  MEETING_IN_PERSON = 'MEETING_IN_PERSON',
  VIDEO_CALL = 'VIDEO_CALL',
  OTHER_MEANS = 'OTHER_MEANS',
}

export interface InvestorKycFactrisIdentityDTO {
  factrisIdentificationApproved: boolean
  factrisIdentificationApprovedAt: Date
  factrisIdentificationApprovedBy: string
  factrisIdentificationApprovedByEmail: string
  factrisIdentificationComment: string
  factrisIdentificationType: ManualKycIdentificationType
}

export interface InvestorKycIdentityDetailsDTO {
  birthYear: number
  country: string
  email: string
  id: string
  kycIdentificationType: KycIdentificationType
  name: string
  role: KycIdentityRole
  surname: string
  investorId: string
  investorType: InvestorType
  communicationCountry: string
}

export interface InvestorKycIdentitiesRiskQuestionnaireDTO {
  identityHitInOndato: boolean
  identityHitInOndatoComment: string
}

export interface InvestorKycIdentityOndatoDTO {
  ondatoBackOfficeStatusUrl: string
  questionnaireId: string
  questionnaireUrl: string
  status: KycQuestionnaireStatus
  failureReason: KycFailureReason
}

export interface InvestorsKycIdentityLogDTO {
  id: string
  createdAt: Date
  event: AuditActions
  userFullName: string
}

export interface InvestorKycIdentityProfileDTO {
  investorStatus: InvestorStatus
  identityDetails: InvestorKycIdentityDetailsDTO
  factrisIdentity: InvestorKycFactrisIdentityDTO
  ondato: InvestorKycIdentityOndatoDTO
  riskQuestionnaire: InvestorKycIdentitiesRiskQuestionnaireDTO
  logs: Array<InvestorsKycIdentityLogDTO>
}

export interface InvestorKycIdentityDetailsData {
  investorKycIdentityDetails: InvestorKycIdentityProfileDTO
}

export interface InvestorKycIdentityDetailsVariables {
  identityId: string
}

export enum UpdateInvestorKycIdentitiesIdentificationFieldsNames {
  KYC_IDENTIFICATION_TYPE = 'kycIdentificationType',
}

export interface UpdateInvestorKycIdentitiesIdentificationFormValues {
  kycIdentificationType: KycIdentificationType
}

export interface UpdateInvestorKycIdentitiesIdentificationVariables extends UpdateInvestorKycIdentitiesIdentificationFormValues {
  identityId: string
}

export interface UpdateInvestorKycIdentitiesIdentificationData {
  updateInvestorKycIdentitiesIdentification: InvestorKycIdentityProfileDTO
}

export enum UpdateInvestorKycIdentitiesRiskQuestionnaireFieldsNames {
  HIT_IDENTITY_IN_ONDATO = 'identityHitInOndato',
  HIT_IDENTITY_IN_ONDATO_COMMENT = 'identityHitInOndatoComment',
}

export interface UpdateInvestorKycIdentitiesRiskQuestionnaireFormValues {
  identityHitInOndatoComment: string
  identityHitInOndato: boolean
}

export interface UpdateInvestorKycIdentitiesRiskQuestionnaireVariables extends UpdateInvestorKycIdentitiesRiskQuestionnaireFormValues {
  identityId: string
}

export interface UpdateInvestorKycIdentitiesRiskQuestionnaireData {
  updateInvestorKycIdentitiesRiskQuestionnaire: InvestorKycIdentityProfileDTO
}

export enum UpdateInvestorKycIdentitiesFactrisIdentificationFieldsNames {
  FACTRIS_IDENTIFICATION_APPROVED = 'factrisIdentificationApproved',
  FACTRIS_IDENTIFICATION_COMMENT = 'factrisIdentificationComment',
  FACTRIS_IDENTIFICATION_TYPE = 'factrisIdentificationType',
}

export interface UpdateInvestorKycIdentitiesFactrisIdentificationFormValues {
  factrisIdentificationApproved: boolean
  factrisIdentificationComment: string
  factrisIdentificationType: ManualKycIdentificationType
}

export interface UpdateInvestorKycIdentitiesFactrisIdentificationVariables
  extends UpdateInvestorKycIdentitiesFactrisIdentificationFormValues {
  identityId: string
}

export interface UpdateInvestorKycIdentitiesFactrisIdentificationData {
  updateInvestorKycIdentitiesFactrisIdentification: InvestorKycIdentityProfileDTO
}

export enum InvestmentStatus {
  ACTIVE = 'ACTIVE',
  EXPIRING_SOON = 'EXPIRING_SOON',
  EXPIRED = 'EXPIRED',
  WITHDRAWN = 'WITHDRAWN',
}

export enum InvestmentPlanFilterStatus {
  ALL = 'ALL',
  ACTIVE = 'ACTIVE',
  EXPIRING_SOON = 'EXPIRING_SOON',
  EXPIRED = 'EXPIRED',
  WITHDRAWN = 'WITHDRAWN',
}

export enum ReinvestmentStatus {
  BOOKED = 'BOOKED',
  CANCELED = 'CANCELED',
  FINISHED = 'FINISHED',
  NOT_SIGNED = 'NOT_SIGNED',
}

export interface InvestmentPlanDTO {
  annualInterestRate: number
  earlyWithdrawalRate: number
  endDate: Date
  id: string
  interestPayoutFrequency: OfferPayoutFrequency
  investedAmount: number
  investmentNumber: string
  investmentStatus: InvestmentStatus | ReinvestmentStatus
  investmentTerm: number
  investorId: string
  investorsName: string
  lastWithdrawalDate: Date
  minInvestmentAmount: number
  offerId: string
  offerNumber: string
  offerType: OfferType
  openInvestedAmount?: number
  repaidInterestAmount?: number
  repaidInvestedAmount?: number
  startDate: Date
  totalAccruedInterestAmount: number
}

export interface Page_InvestmentPlanDTO {
  content: Array<InvestmentPlanDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface InvestmentPlanData {
  investmentPlanList: Page_InvestmentPlanDTO
}

export interface InvestmentPlanVariables {
  investorId?: string
  offerId?: string
  totalAmountInvest?: MinMaxFilterInput
  status?: Array<InvestorStatus>
  tableStatus: InvestmentPlanFilterStatus
  offerType?: Array<OfferType>
  sortDirection?: Direction
  sortField?: string
  pageSize?: number
  pageNum?: number
}

export enum CreateNewInvestmentPlanFieldsNames {
  OFFER_ID = 'offerId',
  AMOUNT_UNDER_INVESTOR = 'availableAmountUnderInvestor',
  AMOUNT_UNDER_OFFER = 'availableAmountUnderOffer',
  MIN_AMOUNT = 'minAmountToInvest',
  AMOUNT = 'amount',
}

export interface CreateNewInvestmentPlanFormValues {
  offerId?: string
  amount: number
  availableAmountUnderOffer: number
  availableAmountUnderInvestor: number
  minAmountToInvest: number
}

export interface CreateNewInvestmentPlanVariables {
  investorId: string
  offerId: string
  amount: number
  investmentTaskId?: string
}

export interface CreateNewInvestmentPlanData {
  createNewInvestmentPlan: InvestorKycIdentityProfileDTO
}

export interface InvestmentPlanLogDTO {
  id: string
  createdAt: Date
  event: AuditActions
  userFullName: string
  interestPaymentStartDate: Date
  interestPaymentEndDate: Date
  interestPaymentAmount: number
}

export enum InvestorPlanPaymentType {
  INTEREST_PAYMENT = 'INTEREST_PAYMENT',
  INVESTMENT = 'INVESTMENT',
  REPAYMENT = 'REPAYMENT',
  RETURN_PAYMENT = 'RETURN_PAYMENT',
  WITHDRAWAL = 'WITHDRAWAL',
  WITHDRAWAL_BEFORE_TERM = 'WITHDRAWAL_BEFORE_TERM',
}

export interface InvestmentPlanWithdrawalDTO {
  amount: number
  comment: string
  date: Date
  id: string
  paymentType: InvestorPlanPaymentType
  penalty: number
  withdrawalPaymentStatus: InvestorWithdrawalPaymentStatus
  paymentNumber: string
}

export interface InvestmentPlanWithdrawalDetailsDTO {
  withdrawals: Array<InvestmentPlanWithdrawalDTO>
}

export interface InvestmentPlanDetailsDTO {
  investorStatus: InvestorStatus
  accruedInterests: number
  paidInterests: number
  unpaidInterests: number
  investmentPlan: InvestmentPlanDTO
  interestPayouts: Array<InterestPayoutDTO>
  withdrawalDetails: InvestmentPlanWithdrawalDetailsDTO
  logs: Array<InvestmentPlanLogDTO>
}

export interface InvestmentPlanDetailsData {
  investmentPlanDetails: InvestmentPlanDetailsDTO
}

export interface InvestmentPlanDetailsVariables {
  investmentPlanId: string
}

export enum UpdateInvestmentPlanCommercialTermsFieldsNames {
  EARLY_WITHDRAWAL_RATE = 'earlyWithdrawalRate',
}

export interface UpdateInvestmentPlanCommercialTermsFormValues {
  earlyWithdrawalRate: number
}

export interface UpdateInvestmentPlanCommercialTermsVariables extends UpdateInvestmentPlanCommercialTermsFormValues {
  investmentPlanId: string
}

export interface UpdateInvestmentPlanCommercialTermsData {
  updateInvestmentPlanCommercialTerms: InvestmentPlanDTO
}

export enum WithdrawInvestorsBalanceFieldsNames {
  ACTION = 'withdrawalAction',
  INVESTMENT_PLAN_ID = 'investmentPlanId',
  AVAILABLE_AMOUNT = 'availableAmount',
  OPEN_AMOUNT_INVESTED = 'openInvestedAmount',
  WITHDRAW_AMOUNT = 'amount',
  EARLY_WITHDRAWAL_RATE = 'earlyWithdrawalRate',
  COMMENT = 'comment',
}

export enum InvestorWithdrawalAction {
  WITHDRAW_AVAILABLE = 'WITHDRAW_AVAILABLE',
  WITHDRAW_BEFORE_TERM = 'WITHDRAW_BEFORE_TERM',
}

export interface WithdrawInvestorsBalanceFormValues {
  amount: number
  availableAmount: number
  openInvestedAmount: number
  earlyWithdrawalRate: number
  investmentPlanId: string
  withdrawalAction: InvestorWithdrawalAction
  comment: string
}

export interface WithdrawInvestorsBalanceVariables {
  investorId: string
  amount: number
  investmentPlanId: string
  withdrawalAction: InvestorWithdrawalAction
  comment: string
}

export interface WithdrawInvestorsBalanceData {
  withdrawInvestorsBalance: InvestorDetailsDTO
}

export interface Page_InterestPayoutDTO {
  content: Array<InterestPayoutDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface WithdrawalBeforeTermPayoutsData {
  withdrawalBeforeTermPayoutList: Page_InterestPayoutDTO
}

export interface WithdrawalBeforeTermPayoutsVariables {
  payoutAmount?: MinMaxFilterInput
  investmentNumber?: string
  sortDirection?: Direction
  amount?: MinMaxFilterInput
  investorsName?: string
  sortField?: string
  pageSize?: number
  penaltyAmount?: MinMaxFilterInput
  pageNum?: number
  paymentNumber?: string
  status?: InvestorWithdrawalPaymentStatus[]
}

export interface DownloadWithdrawalBeforeTermNoteData {
  downloadWithdrawalBeforeTermNote: ReportFileDTO
}

export interface DownloadWithdrawalBeforeTermNoteVariables {
  id: string
}

export enum InvestmentTaskStatus {
  ARCHIVED = 'ARCHIVED',
  CLOSED = 'CLOSED',
  REJECTED = 'REJECTED',
  OPEN = 'OPEN',
}

export enum InvestmentTaskType {
  REINVESTING = 'REINVESTING',
  AVAILABLE_BALANCE = 'AVAILABLE_BALANCE',
  ONBOARDING_APPLICATION = 'ONBOARDING_APPLICATION',
  WITHDRAWAL_REQUEST = 'WITHDRAWAL_REQUEST',
  INVESTMENT_REQUEST = 'INVESTMENT_REQUEST',
}

export interface InvestmentTaskDTO {
  archivedAt: Date
  archivedBy: string
  assignedTo: string
  closedAt: Date
  closedBy: string
  createdOn: Date
  dueDays: number
  id: string
  investmentAmount: number
  investorId: string
  investorsName: string
  status: InvestmentTaskStatus
  taskType: InvestmentTaskType
}

export interface Page_InvestmentTaskDTO {
  content: Array<InvestmentTaskDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface InvestmentTasksData {
  investmentTaskList: Page_InvestmentTaskDTO
}

export enum InvestmentTaskFilterStatus {
  ALL = 'ALL',
  ARCHIVED = 'ARCHIVED',
  ASSIGNED_TO_ME = 'ASSIGNED_TO_ME',
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
  UNASSIGNED = 'UNASSIGNED',
  REJECTED = 'REJECTED',
}

export interface InvestmentTasksVariables {
  closedBy?: string
  investmentAmount?: MinMaxFilterInput
  createdOn?: FromToFilterInput
  assignedTo?: string
  archivedAt?: FromToFilterInput
  taskType?: [InvestmentTaskType]
  investorsName?: string
  taskFilter: InvestmentTaskFilterStatus
  closedAt?: FromToFilterInput
  archivedBy?: string
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface OperationUserDTO {
  email: string
  fullName: string
  id: string
}

export interface InvestmentTaskManagersData {
  getInvestmentTaskManagers: Array<OperationUserDTO>
}

export interface AssignInvestmentTasksData {
  assignInvestmentTask: boolean
}

export interface AssignInvestmentTasksVariables {
  tasks: Array<string>
  assignee: string
}

export interface AssignInvestmentTasksToMeData {
  assignInvestmentTaskToMe: boolean
}

export interface AssignInvestmentTasksToMeVariables {
  tasks: Array<string>
}

export enum AssignTasksFieldNames {
  TASKS = 'tasks',
  ASSIGNEE = 'assignee',
}

export interface InvestmentTaskLogDTO {
  assignedTo: string
  assignedToEmail: string
  comments: string
  createdAt: Date
  event: AuditActions
  id: string
  information: string
  investmentTaskId: string
  status: InvestmentTaskStatus
  userFullName: string
}

export enum InvestmentTaskDocumentStatus {
  GENERATED = 'GENERATED',
  NOT_GENERATED = 'NOT_GENERATED',
  SENT_TO_INVESTOR = 'SENT_TO_INVESTOR',
  SIGNED = 'SIGNED',
}

export interface ReinvestmentPlanDTO {
  annualInterestRate: number
  documentStatus: InvestmentTaskDocumentStatus
  endDate: Date
  investmentAmount: number
  investmentPlanId: string
  investmentNumber: number
  investmentTerm: number
  offerId: string
  offerNumber: string
  signatureRequired: boolean
  signedDocumentId: string
  signedDocumentName: string
  startDate: Date
}

export interface InvestmentTaskSettingsDTO {
  interestPayoutFrequency: OfferPayoutFrequency
  settingsAutoProlongation: boolean
  settingsReinvestInterest: boolean
}

export interface InvestmentTaskReinvestmentDetailsDTO {
  totalAvailableAmount: number
  totalAvailableToReinvest: number
  totalReinvestedAmount: number
  reinvestments: Array<ReinvestmentPlanDTO>
}

export interface InvestmentTaskDetailsInfoDTO {
  dueDate: string
  archivedAt: Date
  archiveComment: string
  rejectComment: string
  closedAt: Date
  information: string
  status: InvestmentTaskStatus
  taskType: InvestmentTaskType
}

export enum InvestorLearnAboutFactris {
  ADVERTISEMENT_ONLINE = 'ADVERTISEMENT_ONLINE',
  FD_NL = 'FD_NL',
  OWN_NETWORK = 'OWN_NETWORK',
  RADIO = 'RADIO',
  SOCIAL_MEDIA = 'SOCIAL_MEDIA',
  WEBSITE = 'WEBSITE',
}

export interface InvestorOnboardingApplication {
  email: string
  firstName: string
  lastName: string
  learnAboutFactris: InvestorLearnAboutFactris
  phone: string
}

export interface InvestmentTaskInvestmentRequestDTO {
  amountAvailableUnderInvestor: number
  amountAvailableUnderOffer: number
  amountToInvest: number
  interestPayoutFrequency: OfferPayoutFrequency
  investorsBalanceAmount: number
  investorsVerifiedOriginOfMoneyAmount: number
  minInvestmentAmount: number
  offerId: string
  offerNumber: string
}

export interface InvestmentTaskDetailsDTO {
  onboardingUserCreated: boolean
  onboardingApplication: InvestorOnboardingApplication
  commercialTerms: InvestmentPlanDTO
  contactInfo: InvestorContactInfoDTO
  id: string
  investmentNumber: string
  investmentPlanId: string
  investorId: string
  investorsName: string
  logs: Array<InvestmentTaskLogDTO>
  reinvestmentDetails: InvestmentTaskReinvestmentDetailsDTO
  settings: InvestmentTaskSettingsDTO
  taskDetails: InvestmentTaskDetailsInfoDTO
  investor: InvestorDTO
  withdrawalAmount: number
  withdrawalComment: string
  withdrawalIban: string
  investmentPlans: Array<InvestmentPlanDTO>
  investmentRequest: InvestmentTaskInvestmentRequestDTO
}

export interface InvestmentTaskDetailsData {
  investmentTaskDetails: InvestmentTaskDetailsDTO
}

export interface InvestmentTaskDetailsVariables {
  investmentTaskId: string
}

export interface CreateInvestmentTaskData {
  createInvestmentTask: boolean
}

export interface CreateInvestmentTaskVariables {
  investmentPlanId: string
}

export interface CreateInvestmentAvailableBalanceTaskData {
  createInvestmentAvailableBalanceTask: boolean
}

export interface CreateInvestmentAvailableBalanceTaskVariables {
  investorId: string
}

export interface MarkAsDoneInvestmentTaskData {
  markAsDoneInvestmentTask: InvestmentTaskDetailsDTO
}

export interface MarkAsDoneInvestmentTaskVariables {
  investmentTaskId: string
}

export interface ArchiveInvestmentTaskData {
  archiveInvestmentTask: InvestmentTaskDetailsDTO
}

export interface ArchiveInvestmentTaskVariables {
  investmentTaskId: string
  comment: string
}

export interface RejectInvestmentTaskData {
  rejectInvestmentTask: InvestmentTaskDetailsDTO
}

export interface RejectInvestmentTaskVariables {
  investmentTaskId: string
  comment: string
}

export interface ResetToNewInvestmentTaskData {
  resetToNewInvestmentTask: InvestmentTaskDetailsDTO
}

export interface ResetToNewInvestmentTaskVariables {
  investmentTaskId: string
}

export interface RescheduleInvestmentTaskData {
  rescheduleInvestmentTask: InvestmentTaskDetailsDTO
}

export interface RescheduleInvestmentTaskVariables {
  investmentTaskId: string
  dueDate: string
  information: string
}

export interface AddReinvestmentForInvestmentTaskData {
  addReinvestmentForInvestmentTask: InvestmentTaskDetailsDTO
}

export interface AddReinvestmentFormValues {
  amount: number
  offerId: string
  totalAvailableToReinvest: number
  minAmountToInvest: number
  availableAmountUnderOffer: number
  annualInterestRate: number
  endDate: Date
}

export enum AddReinvestmentFieldsNames {
  OFFER_ID = 'offerId',
  ANNUAL_INTEREST_RATE = 'annualInterestRate',
  AVAILABLE_AMOUNT_TO_REINVEST = 'totalAvailableToReinvest',
  AMOUNT_UNDER_OFFER = 'availableAmountUnderOffer',
  MIN_AMOUNT = 'minAmountToInvest',
  AMOUNT = 'amount',
  END_DATE = 'endDate',
}

export interface AddReinvestmentForInvestmentTaskVariables {
  investmentTaskId: string
  investmentAmount: number
  offerId: string
}

export interface CancelReinvestmentForInvestmentTaskData {
  cancelReinvestmentForInvestmentTask: InvestmentTaskDetailsDTO
}

export interface CancelReinvestmentForInvestmentTaskFormData {
  comment?: string
}

export enum CancelReinvestmentForInvestmentTaskFieldsNames {
  COMMENT = 'comment',
}

export interface CancelReinvestmentForInvestmentTaskVariables extends CancelReinvestmentForInvestmentTaskFormData {
  investmentPlanId: string
}

export interface UploadInvestmentTaskSignedDocumentData {
  uploadInvestmentTaskSignedDocument: InvestmentTaskDetailsDTO
}

export interface UploadInvestmentTaskSignedDocumentVariables {
  investmentPlanId: string
  signedDocument: ReportFileDTOInput
}

export interface UploadInvestmentTaskSignedDocumentFormData {
  signedDocument: ReportFileDTOInput
}

export enum UploadInvestmentTaskSignedDocumentFieldsNames {
  FILE = 'signedDocument',
}

export interface SendDocumentOrEmailForm {
  email: string
}

export enum SendDocumentOrEmailFieldNames {
  EMAIL = 'email',
}

export interface SendDocumentForSigningVariables extends SendDocumentOrEmailForm {
  investmentPlanId: string
}

export interface SendDocumentForSigningData {
  sendLoanBookDocumentForSigning: LoanBookProductDetailsDTO
}

export interface SendNotificationVariables extends SendDocumentOrEmailForm {
  investmentPlanId: string
}

export interface SendNotificationData {
  sendLoanBookDocumentForSigning: LoanBookProductDetailsDTO
}

export interface GeneratedReinvestmentPlanSpecialConditionsData {
  generatedReinvestmentPlanSpecialConditions: ReportFileDTO
}

export interface GeneratedReinvestmentPlanSpecialConditionsVariables {
  investmentPlanId: string
}

export interface ReinvestmentPlanLogDTO {
  comments: string
  createdAt: Date
  event: AuditActions
  id: string
  userFullName: string
  email: string
}

export interface ReinvestmentPlanDetailsDTO {
  canceled: boolean
  documentStatus: InvestmentTaskDocumentStatus
  commercialTerms: InvestmentPlanDTO
  contactInfo: InvestorContactInfoDTO
  investmentPlanId: string
  investmentNumber: string
  logs: Array<ReinvestmentPlanLogDTO>
  signatureRequired: boolean
  signedDocumentId: string
  signedDocumentName: string
  taskId: string
  taskType: InvestmentTaskType
  taskStatus: InvestmentTaskStatus
}

export interface ReinvestmentPlanDetailsData {
  reinvestmentPlanDetails: ReinvestmentPlanDetailsDTO
}

export interface ReinvestmentPlanDetailsVariables {
  investmentPlanId: string
}

export interface InvestorReportDTO {
  documentId: string
  fileName: string
  createdOn: Date
  status: ReportStatus
  uploader: string
}

interface Page_ReportDTO {
  content: Array<InvestorReportDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface InvestorReportsData {
  listInvestorReports: Page_ReportDTO
}

export interface InvestorReportsVariables {
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface DownloadInvestorReportData {
  downloadInvestorReport: ReportFileDTO
}

export interface DownloadInvestorReportVariables {
  documentId: string
}

export interface CancelInvestorReportData {
  cancelInvestorReport: boolean
}

export interface CancelInvestorReportVariables {
  reportId: string
}

export interface RequestGenerateInvestorsReportData {
  requestInvestorReport: string
}

export interface RequestGenerateInvestorsReportVariables {
  country?: string
  totalAmountInvest?: MinMaxFilterInput
  name?: string
  identificationNumber?: string
  tableStatus?: InvestorFilterStatus
  status?: InvestorStatus[]
  investorType?: InvestorType[]
}

export interface InvestmentReportsData {
  listInvestmentReports: Page_ReportDTO
}

export interface InvestmentReportsVariables {
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface DownloadInvestmentReportData {
  downloadInvestmentReport: ReportFileDTO
}

export interface DownloadInvestmentReportVariables {
  documentId: string
}

export interface CancelInvestmentReportData {
  cancelInvestmentReport: boolean
}

export interface CancelInvestmentReportVariables {
  reportId: string
}

export interface RequestGenerateInvestmentReportData {
  requestInvestmentReport: string
}

export interface RequestGenerateInvestmentReportVariables {
  offerType?: OfferType[]
  interestPayoutFrequency?: OfferPayoutFrequency[]
  investmentNumber?: string
  investmentStatus?: InvestmentStatus[]
  investorId?: string
  offerId?: string
  investedAmount?: MinMaxFilterInput
  offerNumber?: string
  tableStatus?: InvestmentPlanFilterStatus
}

export interface AssignInvestorManagerToInvestorData {
  assignInvestorManagerToInvestor: InvoiceDetailsDTO
}

export interface AssignInvestorManagerToInvestorFormValues {
  email: string
}

export enum AssignInvestorManagerToInvestorFieldsNames {
  EMAIL = 'email',
}

export interface AssignInvestorManagerToInvestorVariables {
  investorId: string
  email: string
}

export interface UserDTO {
  email: string
}

export interface InvestorManagersData {
  getInvestorManagers: Array<OperationUserDTO>
}

export interface InvestorAnnualReportDTO {
  content: string
  createdOn: Date
  documentId: string
  fileName: string
  investorId: string
  investorName: string
  status: ReportStatus
  totalAccrued: number
  totalPaidOut: number
  totalReinvested: number
  uploader: string
}

interface Page_InvestorAnnualReportDTO {
  content: InvestorAnnualReportDTO[]
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface AnnualIncomeDeclarationData {
  listInvestorAnnualReports: Page_InvestorAnnualReportDTO
}

export interface AnnualIncomeDeclarationVariables {
  createdOn?: FromToFilterInput
  fileName?: string
  totalPaidOut?: MinMaxFilterInput
  totalAccrued?: MinMaxFilterInput
  investorName?: string
  totalReinvested?: MinMaxFilterInput
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface DownloadAnnualIncomeDeclarationData {
  downloadInvestorAnnualReport: ReportFileDTO
}

export interface DownloadAnnualIncomeDeclarationVariables {
  documentId: string
}

export interface CancelAnnualIncomeDeclarationData {
  cancelInvestorAnnualReport: boolean
}

export interface CancelAnnualIncomeDeclarationVariables {
  reportId: string
}

export interface GenerateAnnualIncomeDeclarationData {
  requestInvestorAnnualReport: AccountingNoteReportResponseDTO
}

export interface GenerateAnnualIncomeDeclarationVariables {
  year: number
  investorIds: string[]
}

export enum GenerateAnnualIncomeDeclarationFieldNames {
  YEAR = 'year',
  INVESTORS_IDS = 'investorIds',
}
